// Phase Response Card Button Shadow MVP — paper-lift shadow math.
//
// Reads `customStyle.buttonShadowStrength` (0..100, default 0) and emits
// CSS variable values for the rest and pressed states of the RSVP submit
// button. The button uses the shared `.rsvp-submit-shadow` class so the
// `:active` state can override the rest shadow without JS event handling.
//
// Updated formula v3 (May 27, 2026) — switched from warm-brown to neutral
// pure-black tint. The brown was getting swallowed by ivory / warm-paper
// backgrounds on real mobile, so at strength 100 the button still read as
// "subtle 40". Pure black holds its definition against warm backgrounds
// and matches the editorial-card standard. Alphas + blur bumped so:
//   0   = none
//   30  = subtle
//   60  = visible paper lift
//   100 = clearly visible editorial paper lift
// Direction stays locked downward.
//
// Math:
//   k = clamp(strength / 100, 0, 1)
//   restShadow:
//     0 (2*k)px (4*k)px rgba(0,0,0, 0.12 * k),
//     0 (14*k)px (36*k)px rgba(0,0,0, 0.28 * k)
//   activeShadow:
//     0 (1*k)px (2*k)px rgba(0,0,0, 0.10 * k),
//     0 (7*k)px (20*k)px rgba(0,0,0, 0.18 * k)

const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0));

function tint(alpha) {
  return `rgba(0, 0, 0, ${alpha.toFixed(3)})`;
}

function buildRestShadow(k) {
  if (k <= 0) return 'none';
  return (
    `0 ${(2 * k).toFixed(2)}px ${(4 * k).toFixed(2)}px ${tint(0.12 * k)}, ` +
    `0 ${(14 * k).toFixed(2)}px ${(36 * k).toFixed(2)}px ${tint(0.28 * k)}`
  );
}

function buildActiveShadow(k) {
  if (k <= 0) return 'none';
  return (
    `0 ${(1 * k).toFixed(2)}px ${(2 * k).toFixed(2)}px ${tint(0.10 * k)}, ` +
    `0 ${(7 * k).toFixed(2)}px ${(20 * k).toFixed(2)}px ${tint(0.18 * k)}`
  );
}

/**
 * Build the inline style fragment to spread onto an RSVP submit button.
 * Combines with the `.rsvp-submit-shadow` CSS class which toggles between
 * the two CSS variables on `:active`.
 *
 * @param {number} strength — 0..100 (default 0)
 * @returns {object} style fragment with the CSS variables set
 */
export function getRsvpButtonShadowStyle(strength) {
  const k = clamp01(Number(strength) / 100);
  return {
    '--rsvp-btn-shadow-rest': buildRestShadow(k),
    '--rsvp-btn-shadow-active': buildActiveShadow(k),
  };
}

// Exposed for unit tests / Storybook tinkering.
export const __testables__ = { clamp01, buildRestShadow, buildActiveShadow };
